import { Actions, ListFields, PaginationList } from "../types";
import { useTranslation } from "react-i18next";
import ModalGlobal from '../../../../components/AppModal/ModalGlobal';
import { useModal } from "../../../../hooks/useModal";
import { exportCsvMotiveReopenClaim } from "../api/motiveReopenClaim";
import { useAlert } from "../../../../hooks/useAlert";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { isInactiveState } from "../../../../utils/activeStateUtils";
import { getTranslatedDescription } from "../../../../utils/formatters/UserLanguageToDescriptionField";

interface ListTableProps {
    data: PaginationList;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    genericActions: Actions;
    refetchData: () => void;
    onImportCsv?: () => void;
}

export default function ListTable({ data, genericActions, pageSize, refetchData, currentPage, totalPages, onImportCsv }: ListTableProps) {
    const { t, i18n } = useTranslation();

    const PARTICIPANT_MODAL_HEADERS = {
        titleDelete: t("genericDic:modal.modalHeaderDelete"),
    };

    const {showModal, modalData, errorModal, endDate, openModal: openDeleteWithoutImputModal, openModal: openActivateModal, closeModal, setLoading,
        isLoading,
        currentItem
    } = useModal<ListFields>();

    const { showAlert } = useAlert({ fromLocation: true });

    const tableName = (data.table ? data.table.charAt(0).toLowerCase() + data.table.slice(1) : "");
    const tableLabel = t(`genericDic:${tableName}`).toLowerCase();

    const handleDelete = async (genericList: ListFields) => {
        openDeleteWithoutImputModal(
            genericList,
            PARTICIPANT_MODAL_HEADERS.titleDelete,
            `${t("common:buttons.buttonDelete")} ${tableLabel}: ${getTranslatedDescription(genericList.description, i18n.language)}?`,
            "deleteWithoutInput",
            ""
        );
    };

    const handleSaveModal = async () => {
        setLoading(true);

        try {
            if (modalData.type === "deleteWithoutInput") {
                await genericActions.onDelete(currentItem!.id, tableName);
                await refetchData();
            }

            if (modalData.type === "active") {
                await genericActions.onActive(currentItem!.id, tableName);
                await refetchData();
            }

            closeModal();
        } catch {
            closeModal();
            showAlert({
                message: "Error",
                type: "danger",
                duration: 8000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleViewEvent = (id: number) => {
        genericActions.onView(id);
    };

    const handleActive = (genericList: ListFields) => {
        openActivateModal(
            genericList,
            t("genericDic:modal.modalHeaderActiveTitle"),
            t("genericDic:modal.modalHeaderActiveSubtitle", { description: getTranslatedDescription(genericList.description, i18n.language) }),
            "active",
            ""
        );
    };

    const handleExportCsv = () => {
        try {
            exportCsvMotiveReopenClaim();
            closeModal();
            showAlert({
                message: t("Success"),
                type: "success",
                duration: 8000
            });
        } catch {
            showAlert({
                message: "Error",
                type: "danger",
                duration: 8000
            });
        }
    };

    const getStateLabel = (state?: string) =>
        isInactiveState(state) ? t("common:stateInactive") : t("common:stateActive");

    const sectionPermissionByTable: Record<string, SectionPermissionTypeEnum> = {
        claimReopenReason: SectionPermissionTypeEnum.COMPLAINT_REOPEN_REASON,
        demandInFormat: SectionPermissionTypeEnum.DEMAND_IN_FORMAT,
        demandOutFormat: SectionPermissionTypeEnum.DEMAND_OUT_FORMAT,
        demandDerivation: SectionPermissionTypeEnum.DEMAND_DERIVATION,
        claimOrigin: SectionPermissionTypeEnum.COMPLAINT_CLOSE_REASON,
        claimDeleteReason: SectionPermissionTypeEnum.COMPLAINT_CLOSE_REASON,
        surveyClosedReason: SectionPermissionTypeEnum.COMPLAINT_CLOSE_REASON,
        claimProfessionalType: SectionPermissionTypeEnum.COMPLAINT_CLOSE_REASON,
        improvementPlanType: SectionPermissionTypeEnum.IMPROVEMENT_PLANS,
        improvementPlanOrigin: SectionPermissionTypeEnum.IMPROVEMENT_PLANS,
        improvementPlanFinalizeReasons: SectionPermissionTypeEnum.IMPROVEMENT_PLANS,
        improvementPlanDeleteReasons: SectionPermissionTypeEnum.IMPROVEMENT_PLANS,
        functionalAreas: SectionPermissionTypeEnum.FUNCTIONAL_AREAS,
        serviceIndicators: SectionPermissionTypeEnum.SERVICES,
        TopicManagement: SectionPermissionTypeEnum.TOPIC_MANAGEMENT,
        campaignClosedReason: SectionPermissionTypeEnum.CAMPAIGN_CLOSED_REASON,
        reportDefinitions: SectionPermissionTypeEnum.REPORT_DEFINITIONS,
    };

    const sectionPermission = sectionPermissionByTable[data.table] ?? SectionPermissionTypeEnum.COMPLAINT_CLOSE_REASON;

    const columns: ColumnConfig<ListFields>[] = [
        {
            header: t("genericDic:listHeaderId"),
            key: "id",
            render: (item) => <>{item.id}</>
        },
        {
            header: t("genericDic:listHeaderDescription"),
            key: "description",
            render: (item) => <>{getTranslatedDescription(item.description, i18n.language)}</>
        },
        {
            header: t("genericDic:listHeaderStatus"),
            key: "state",
            render: (item) => (
                <span style={{ color: isInactiveState(item.state) ? "gray" : "inherit" }}>
                    {getStateLabel(item.state)}
                </span>
            )
        }
    ];
    if (data.table == "surveyClosedReason") {
        
        columns.push({
            header: t("genericDic:listIsAutomaticValues"),
            key: "isAutomaticValue",
            render: (item) => <>{item.isAutomaticValue ? t("common:stateActive") : t("common:stateInactive")}</>
        });
    }

    return (
        <div>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                endDate={endDate}
                onSave={() => handleSaveModal()}
                isLoading={isLoading}
            />

            <AppTable<ListFields>
                data={data.items}
                columns={columns}
                emptyMessage={t("genericDic:nonRegisters")}
                t={t}
                hasActions={true}
                onDelete={handleDelete}
                onView={handleViewEvent}
                onActive={handleActive}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                paginationType={data.table}
                onExportCsv={handleExportCsv}
                showImportLink={!!onImportCsv}
                onImportCsv={onImportCsv}
                sectionPermission={sectionPermission}
            />
        </div>
    );
}
